/* Collection readiness and pending review activation; native setup takes ownership. */
(() => {
  'use strict';
  if (window.rawryCollectionLoading) return;
  const selector = '#CollectionSection[data-rawry-collection-loading]';
  const sections = new Map();
  // A string is our last pending value; null permanently hands this badge to the app.
  const reviewText = new WeakMap();
  const reviewSize = new WeakMap();
  const starFont = '20px JudgemeStar';
  const starsLoaded = () => [...document.fonts].some(face =>
    face.family.replace(/["']/g, '') === 'JudgemeStar' && face.status === 'loaded'
  ) && document.fonts.check(starFont, '\ue000');

  const reviewStars = new WeakMap();
  const reviewLinkLabel = 'See all reviews';
  const validCount = value => !!value && /^[1-9]\d*$/.test(value);
  const ratingValue = value => value?.trim() ? Number(value) : NaN;
  const validRating = value => Number.isFinite(value) && value >= 0 && value <= 5;

  function releaseSize(badge) {
    const size = reviewSize.get(badge);
    if (!size) return;
    size.reserve.remove();
    if (size.value.parentNode === size.text) size.value.replaceWith(...size.value.childNodes);
    size.text.classList.remove('rawry-review-pending');
    reviewSize.delete(badge);
  }

  function pendingSize(badge, text, count, score, label, preserve = false) {
    let size = reviewSize.get(badge);
    if (!size || size.text !== text || size.value.parentNode !== text || size.reserve.parentNode !== text) {
      releaseSize(badge);
      size = {text, value: document.createElement('span'), reserve: document.createElement('span')};
      size.value.className = 'rawry-review-value';
      size.reserve.className = 'rawry-review-size';
      size.reserve.setAttribute('aria-hidden', 'true');
      // On handoff, keep the app's current child content, including its nodes.
      if (preserve) size.value.append(...text.childNodes);
      text.replaceChildren(size.reserve, size.value);
      reviewSize.set(badge, size);
    }
    // Current-count full format; invisible, aria-hidden and font-responsive.
    const fullLabel = `5.0 (${count} Reviews)`;
    if (size.reserve.textContent !== fullLabel) size.reserve.textContent = fullLabel;
    if (!preserve && size.value.textContent !== label) size.value.textContent = label;
    Object.assign(size, {count, score, label});
    text.classList.add('rawry-review-pending');
  }

  function handoffSize(badge, text) {
    const size = reviewSize.get(badge);
    if (!size) return;
    const label = [...text.childNodes].filter(node => node !== size.reserve)
      .map(node => node.textContent).join('').trim();
    // Same source and visible output keep the reservation, regardless of write form.
    if (label === size.label && badge.getAttribute('data-number-of-reviews') === size.count &&
      badge.getAttribute('data-average-rating') === size.score) {
      pendingSize(badge, text, size.count, size.score, label, true);
    } else releaseSize(badge);
  }

  function updateStars(badge, owned, ready) {
    const stars = badge.querySelector('.jdgm-prev-badge__stars');
    if (!stars) return;
    const held = reviewStars.get(stars);
    if (owned && !held) return;
    const score = stars.getAttribute('data-score');
    const label = stars.getAttribute('aria-label');
    const rating = ratingValue(badge.getAttribute('data-average-rating'));
    const nativeRating = ratingValue(score);
    const announced = (held && label === reviewLinkLabel ? held.label : label)?.match(/^([\d.]+) stars$/);
    const qualified = validCount(badge.getAttribute('data-number-of-reviews')) && validRating(rating) &&
      validRating(nativeRating) && rating === nativeRating && (!announced || Number(announced[1]) === rating);
    // A native star write after setup owns the output; source-only changes do not reclaim it.
    const nativeWrite = held && (score !== held.score || stars.innerHTML !== held.markup || label !== reviewLinkLabel);
    if (owned && nativeWrite) {
      Object.assign(held, {score, markup: stars.innerHTML, label, nativeOwned: true});
    }
    if (ready && (qualified || (owned && held?.nativeOwned))) {
      if (held) {
        stars.removeAttribute('data-rawry-review-link');
        if (label === reviewLinkLabel) {
          if (held.label === null) stars.removeAttribute('aria-label');
          else stars.setAttribute('aria-label', held.label);
        }
        reviewStars.delete(stars);
      }
      return;
    }
    if (!held) reviewStars.set(stars, {score, label, markup: stars.innerHTML});
    if (stars.getAttribute('data-rawry-review-link') !== reviewLinkLabel) stars.setAttribute('data-rawry-review-link', reviewLinkLabel);
    if (label !== reviewLinkLabel) stars.setAttribute('aria-label', reviewLinkLabel);
  }

  function update(root) {
    const ready = starsLoaded();
    root.classList.toggle('rawry-stars-ready', ready);
    root.querySelectorAll('.jdgm-prev-badge').forEach(badge => {
      if (badge.closest('.jdgm--done-setup')) reviewText.set(badge, null);
      const owned = reviewText.get(badge) === null;
      updateStars(badge, owned, ready);
      const text = badge.querySelector('.jdgm-prev-badge__text');
      if (!text) return;
      if (owned) {
        handoffSize(badge, text);
        return;
      }
      const count = badge.getAttribute('data-number-of-reviews');
      const score = badge.getAttribute('data-average-rating');
      const previous = reviewText.get(badge);
      if (!validCount(count)) {
        releaseSize(badge);
        if (previous !== undefined && text.textContent.trim() === previous) text.textContent = '';
        reviewText.delete(badge);
        return;
      }
      const rating = ratingValue(score);
      const complete = validRating(rating);
      const value = complete
        ? `${rating.toFixed(1)} (${count} Reviews)` : `${count} Reviews`;
      if (complete) {
        releaseSize(badge);
        if (text.textContent.trim() !== value) text.textContent = value;
      } else pendingSize(badge, text, count, score, value);
      reviewText.set(badge, value);
    });
    root.querySelectorAll('.rawry-swatch-image').forEach(img => {
      img.parentElement.classList.toggle('rawry-swatch-ready', img.complete && img.naturalWidth > 0);
    });
  }
  function init(root) {
    if (sections.has(root)) return;
    const events = new AbortController();
    const activateReview = event => {
      const control = event.target.closest?.('.jdgm-prev-badge__stars');
      const badge = control?.closest('.jdgm-prev-badge');
      if (!badge || badge.closest('.jdgm--done-setup') || reviewText.get(badge) === null) return;
      if (event.type === 'keydown' && (event.repeat || !['Enter', ' '].includes(event.key))) return;
      if (event.type === 'click' && (event.button || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)) return;
      const product = badge.closest('a.grid-product__link');
      if (!product) return;
      const destination = new URL(product.href);
      destination.hash = 'judgeme_product_reviews';
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(destination.href);
    };
    root.addEventListener('click', activateReview, {capture: true, signal: events.signal});
    root.addEventListener('keydown', activateReview, {capture: true, signal: events.signal});
    const observer = new MutationObserver(records => {
      // Observe app handoff, ignoring our own root/swatch readiness classes.
      if (records.some(record => record.attributeName !== 'class' ||
        record.target.matches('.jdgm-preview-badge, .jdgm-prev-badge__stars, .jdgm-star'))) update(root);
    });
    observer.observe(root, {subtree: true, childList: true, characterData: true, attributes: true,
      attributeFilter: ['data-number-of-reviews', 'data-average-rating', 'data-score', 'aria-label', 'class']});
    const loaded = event => { if (event.target.matches?.('.rawry-swatch-image')) update(root); };
    root.addEventListener('load', loaded, {capture: true, signal: events.signal});
    root.addEventListener('error', loaded, {capture: true, signal: events.signal});
    sections.set(root, {events, observer});
    update(root);
  }
  function dispose(root) {
    const state = sections.get(root);
    if (!state) return;
    state.events.abort();
    state.observer.disconnect();
    sections.delete(root);
  }
  function refresh() {
    sections.forEach((_, root) => { if (!root.isConnected) dispose(root); });
    document.querySelectorAll(selector).forEach(init);
    sections.forEach((_, root) => update(root));
  }
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', event => {
    sections.forEach((_, root) => { if (event.target === root || event.target.contains(root)) dispose(root); });
  });
  document.fonts.addEventListener('loading', refresh);
  document.fonts.addEventListener('loadingdone', refresh);
  document.fonts.addEventListener('loadingerror', refresh);
  // Impulse replaces this host's children after an AJAX collection filter.
  const host = document.getElementById('CollectionAjaxResult');
  const hostObserver = new MutationObserver(refresh);
  const watchHost = () => { if (host) hostObserver.observe(host, {childList: true}); refresh(); };
  window.addEventListener('pagehide', () => {
    hostObserver.disconnect();
    sections.forEach((_, root) => dispose(root));
  });
  window.addEventListener('pageshow', watchHost);
  watchHost();
  window.rawryCollectionLoading = {refresh};
  refresh();
  // Loading the existing face works even when its glyphs are initially hidden.
  document.fonts.load(starFont, '\ue000').then(refresh, refresh);
})();
